import * as THREE from "three";
import * as React from "react";
import {createRef} from "react";
import * as gm from "gammacv";
import {CBAREvent, CBARMode} from "../CBARTypes";
import {canvasCreate, getDeviceInfo} from "./Utils";
import {CBARCameraFacing, CBARCameraResolution, CBARContext, CBARFeatureTracking} from "../CBARContext";
import {Point} from "mirada";
import {CBARPipeline} from "./pipeline/CBARPipeline";
import {VerboseLog} from "./GlobalLogger";
import {getConfig} from "../../backend";
import {RenderOptions} from "../components";
import {CBAROrientationControls} from "./CBAROrientationControls";

export type CBARMediaInfo = {
    width:number,
    height:number
}

type CBARMediaViewProps = {
    onMediaReady:(view:CBARMediaView) => void
    onMediaUpdated:(view:CBARMediaView, info:CBARMediaInfo) => void
}

enum CBARMediaViewErrorCode {
    PermissionDenied='PermissionDenied'
}

enum CBARMediaViewErrorLevel {
    Message,
    Warning,
    Critical,
}

type CBARMediaViewError = {
    code:CBARMediaViewErrorCode,
    message?:string,
    level:CBARMediaViewErrorLevel
}

type CBARMediaViewState = {
    error?:CBARMediaViewError
    hasCameraAccess?:boolean
}

export class CBARMediaView extends React.Component<CBARMediaViewProps, CBARMediaViewState> {

    private container = createRef<HTMLDivElement>();
    public canvasTexture = new THREE.Texture(canvasCreate());

    constructor(props:CBARMediaViewProps) {
        super(props);

        this.state = {
            hasCameraAccess:false
        }
    }

    componentDidMount(): void {

    }

    hasInitialized = false;
    device = getDeviceInfo();

    componentDidUpdate(props:CBARMediaViewProps) {

        if (!this.hasInitialized) {
            this.hasInitialized = true;
            this.canvasTexture.minFilter = THREE.LinearFilter;
            this.canvasTexture.magFilter = THREE.LinearFilter;

            const canvas = this.canvasTexture.image;

            canvas.width = 1080;
            canvas.height = 1080;

            this.props.onMediaReady(this)
        }
    }

    protected getPipeline(input: gm.Tensor) : gm.Operation {
        //identity
        return gm.norm(input, 'minmax')
    }

    public loadImage(image:HTMLImageElement) {
        const canvas = this.canvasTexture.image;
        const ctx = canvas.getContext('2d');
        canvas.width = image.width;
        canvas.height = image.height;

        if (ctx) {
            ctx.drawImage(image, 0,0);
            //this.stopVideoCamera();
        }
        //console.log(`Scene image loaded at ${image.width} x ${image.height}`);
        this._mode = CBARMode.Image;

        this.props.onMediaUpdated(this, {width:canvas.width, height:canvas.height})
    }

    private _mode = CBARMode.None;

    public get mode() : CBARMode {
        return this._mode
    }

    public async startVideoCamera(context:CBARContext, tracking: CBARFeatureTracking, facing:CBARCameraFacing, resolution=CBARCameraResolution.Default) {
        const config = await getConfig()

        return new Promise<void>((resolve, reject)=>{
            if (!this.container.current) {
                reject();
                return;
            }
            const container = this.container.current;
            const canvas = this.canvasTexture.image;

            this._mode = CBARMode.Video;
            this._featureTracking = tracking;
            this._facing = facing;

            this.state = {
                hasCameraAccess:true
            }

            try {

                this.canvasTexture.image.width = container.offsetWidth;
                this.canvasTexture.image.height = container.offsetHeight;
                this.canvasTexture.image.pixelRatio = window.devicePixelRatio;

                //console.log(`canvasTexture: ${this.canvasTexture.image.width}x${this.canvasTexture.image.height}`);

                this.pipeline = new CBARPipeline(context, config, canvas, tracking);
                this.pipeline.mirrorOutput = this.device.type === "desktop" || this.pipeline.facing === CBARCameraFacing.User;
                this.pipeline.context.hasGyroscope = this._hasGyroscope;
                this.tryWireOrientationControls();

                //console.log(`Video camera started at ${props.width} x ${props.height}`);

                this.pipeline.start(resolution, facing).then(()=>{

                    //flip view if facing other dir
                    this.canvasTexture.wrapS = THREE.RepeatWrapping;
                    this.canvasTexture.repeat.x = 1;

                    this.setState({ hasCameraAccess: true });

                    this.props.onMediaUpdated(this, {width:canvas.width, height:canvas.height})
                    resolve();
                }).catch(error=>{
                    this.setState({ error: {code:CBARMediaViewErrorCode.PermissionDenied, message:error.message, level:CBARMediaViewErrorLevel.Critical}});
                    this.pipeline?.release();
                    this.pipeline = undefined;
                    reject(error);
                })
            } catch (error:any) {
                this.setState({ error: {code:CBARMediaViewErrorCode.PermissionDenied, message:error.message, level:CBARMediaViewErrorLevel.Critical}});
                reject(error);
            }
        })

    }

    public stopVideoCamera() {
        if (this.pipeline) {
            this.pipeline.release();
            //this.pipeline = undefined;
            this.orientationControls?.dispose();
            this.orientationControls = undefined;
            this._mode = CBARMode.None;
            VerboseLog(`Video camera stopped`)
        }
    }

    private capture:HTMLCanvasElement|undefined = undefined

    public captureImage() : Promise<HTMLImageElement> {

        if (this.pipeline && this.pipeline.stream) {
            this.canvasTexture.image.width;
            const roi = {x:0,y:0,width:this.pipeline.stream.captureWidth, height:this.pipeline.stream.captureHeight};
            //console.log(`pre roi {x:${roi.x}, y:${roi.y}, width:${roi.width}, height:${roi.height}}`)

            const inputAspectRatio = this.pipeline.stream.captureWidth / this.pipeline.stream.captureHeight;
            const outputAspectRatio = this.canvasTexture.image.width / this.canvasTexture.image.height;
            if (inputAspectRatio > outputAspectRatio) {
                roi.width = outputAspectRatio * this.pipeline.stream.captureHeight;
                roi.x = (this.pipeline.stream.captureWidth - roi.width) / 2;
            } else {
                roi.height = this.pipeline.stream.captureWidth / outputAspectRatio;
                roi.y = (this.pipeline.stream.captureHeight - roi.height) / 2;
            }
            //console.log(`Using roi {x:${roi.x}, y:${roi.y}, width:${roi.width}, height:${roi.height}}`)
            this.capture = this.pipeline.captureRawOutput(roi);
        }

        const url = this.capture?.toDataURL();

        return new Promise((resolve, reject) => {
            if (!this.capture || !url) {
                reject("No image");
                return;
            }
            //console.log(`Got canvas at ${this.capture.width}x${this.capture.height}`);
            const img = document.createElement("img");
            img.width = this.capture.width;
            img.height = this.capture.height;

            img.onload = () => {
                VerboseLog(`Image captured at ${img.width} x ${img.height}`);
                // no longer need to read the blob so it's revoked
                resolve(img)
            };

            img.src = url;
        })
    }

    private _featureTracking = CBARFeatureTracking.None;

    public get featureTracking() {
        return this._featureTracking;
    }

    private _facing = CBARCameraFacing.Environment;

    public get facing() {
        return this._facing;
    }

    private pipeline?:CBARPipeline;

    public handleEvent(event: CBAREvent) {
        this.pipeline?.handleEvent(event);
    }

    //https://github.com/opencv/opencv/blob/68d15fc62edad980f1ffa15ee478438335f39cc3/modules/imgproc/src/hough.cpp

    _trackingPoints:Point[] = [];

    private orientationControls?:CBAROrientationControls

    private tryWireOrientationControls = ()=>{
        const camera  = this.pipeline?.context.gl.camera;
        if (camera && !this.orientationControls) {
            this.orientationControls = new CBAROrientationControls(camera);
        }
    }

    private _hasGyroscope = false;

    public requestAccelerometer = () => {
        //https://github.com/marquizzo/three-gimbal
        //only on non desktop devices

        const dm = DeviceMotionEvent as any;

        if (dm?.requestPermission) {
            //ios only
            console.log("Requesting gyroscope permissions");
            dm.requestPermission().then((response: string) => {
                console.log(`Gyroscope got response: ${response}`);
                if (response == 'granted') {
                    this._hasGyroscope = true;
                    if (this.pipeline) {
                        this.pipeline.context.hasGyroscope = true;
                    }
                    this.tryWireOrientationControls();
                } else {
                    //put up some prompt
                    console.log(`Gyroscope not allowed: ${response}`);
                }
            }).catch(()=>{
                console.log("Gyroscope: Some error")
            });
        } else {
            console.log(`Gyroscope is not enabled`);
        }
    }

    update = (renderOptions:RenderOptions) => {

        this.orientationControls?.update();
        this.pipeline?.update(renderOptions);
        this.canvasTexture.needsUpdate = true

        // const rot = this.pipeline?.context.gl.camera.rotation;
        // if (rot && rot.x !== 0) {
        //     console.log(`camera update (${rot.x}, ${rot.y}, ${rot.z})`);
        // }
    };

    refresh() {
        this.pipeline?.refresh();
    }

    render() {
        return <div ref={this.container} className={"media"} style={{position:"fixed", pointerEvents:"none", width:"100%", height:"100%", top:"0", left:"0"}}>
            {/*<div style={{pointerEvents: "all"}} onClick={()=>this.requestAccelerometer()}>*/}
            {/*    Click here to enable gyro even though you didn't before.*/}
            {/*</div>*/}
        </div>
    }
}